import { Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { sameValues, useFormChanged, zodResolver } from "@/lib/form";
import { useCreateGateway, useUpdateGateway, type GatewayInput } from "@/lib/queries";
import type { Gateway } from "@/lib/types";

/**
 * Create / edit a SIP trunk.
 *
 * Five fields are what a carrier hands you on a setup page — username, password, realm, proxy,
 * and whether to register — and they are first-class here because the rest of the platform reads
 * them directly. Everything else FreeSWITCH supports rides in the params list at the bottom, so
 * the form stays gateway-compatible without a control per sofia param.
 *
 * The password is write-only. On edit the field starts empty and an empty field means "keep the
 * stored one": the console never fetches a carrier credential just to render a form it might
 * not save. Reading one back is the "Reveal" action on the list, which is its own request.
 */

const PARAM_NAME = /^[a-z][a-z0-9-]*$/;
// Set through their own fields; the XML renderer drops a duplicate rather than letting it win,
// so a param typed here would be silently discarded. Mirrors GATEWAY_RESERVED_PARAMS in
// services/core-api/src/schemas.py.
const RESERVED = ["username", "password", "realm", "proxy", "register"];

const schema = z.object({
  name: z.string().min(1, "Required").max(100),
  description: z.string().max(500).optional(),
  username: z.string().min(1, "Required").max(200),
  password: z.string().optional(),
  realm: z.string().min(1, "Required"),
  proxy: z.string().optional(),
  register: z.boolean(),
  enabled: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

interface ParamRow {
  key: string;
  name: string;
  value: string;
}

let nextKey = 0;
const rowsFrom = (params: Record<string, string> | undefined): ParamRow[] =>
  Object.entries(params ?? {}).map(([name, value]) => ({ key: String(nextKey++), name, value }));

export function GatewayFormDialog({
  open, onOpenChange, gateway,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  gateway?: Gateway;
}) {
  const editing = Boolean(gateway);
  const toast = useToast();
  const create = useCreateGateway();
  const update = useUpdateGateway();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [params, setParams] = React.useState<ParamRow[]>([]);
  const [paramError, setParamError] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "", description: "", username: "", password: "", realm: "", proxy: "",
      register: false, enabled: true,
    },
  });
  const formChanged = useFormChanged(form);
  // A row added and left blank is skipped on save, so it holds nothing to lose.
  const paramsChanged = !sameValues(
    params
      .filter((row) => row.name.trim() || row.value.trim())
      .map(({ name, value }) => ({ name, value })),
    Object.entries(gateway?.params ?? {}).map(([name, value]) => ({ name, value })),
  );

  React.useEffect(() => {
    if (!open) return;
    setFormError(null);
    setParamError(null);
    setParams(rowsFrom(gateway?.params));
    form.reset(
      gateway
        ? {
            name: gateway.name,
            description: gateway.description ?? "",
            username: gateway.username,
            password: "",
            realm: gateway.realm,
            proxy: gateway.proxy,
            register: gateway.register,
            enabled: gateway.enabled,
          }
        : {
            name: "", description: "", username: "", password: "", realm: "", proxy: "",
            register: false, enabled: true,
          },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gateway]);

  function collectParams(): Record<string, string> | null {
    const out: Record<string, string> = {};
    for (const row of params) {
      const name = row.name.trim();
      if (!name && !row.value.trim()) continue; // an empty row the operator started and left
      if (!PARAM_NAME.test(name)) {
        setParamError(`"${name || "(blank)"}" is not a sofia param name.`);
        return null;
      }
      if (RESERVED.includes(name)) {
        setParamError(`Set "${name}" in its own field above, not as a param.`);
        return null;
      }
      if (name in out) {
        setParamError(`"${name}" is listed twice.`);
        return null;
      }
      out[name] = row.value.trim();
    }
    setParamError(null);
    return out;
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    const collected = collectParams();
    if (collected === null) return;
    const payload: GatewayInput = {
      name: values.name.trim(),
      description: values.description?.trim() || null,
      username: values.username.trim(),
      realm: values.realm.trim(),
      // Blank means "the same host as the realm", which is what a carrier's single "SIP domain"
      // field almost always means; the server fills it in.
      proxy: values.proxy?.trim() || null,
      register: values.register,
      params: collected,
      enabled: values.enabled,
      ...(values.password ? { password: values.password } : {}),
    };
    if (!editing && !values.password) {
      form.setError("password", { message: "Required" });
      return;
    }
    try {
      if (gateway) {
        await update.mutateAsync({ id: gateway.id, ...payload });
        toast.success("Gateway updated", `${payload.name} — the switch is re-reading it now.`);
      } else {
        await create.mutateAsync(payload);
        toast.success("Gateway created", `${payload.name} — the switch is re-reading it now.`);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the gateway.");
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={formChanged || paramsChanged} />
        <DialogHeader>
          <DialogTitle>{editing ? "Edit gateway" : "New gateway"}</DialogTitle>
          <DialogDescription>
            A SIP trunk. Saving one tells the switch to re-read the profile straight away, so the
            change reaches calls without a restart.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <Field
              label="Name"
              htmlFor="gateway-name"
              required
              hint="The label you will pick this trunk by. FreeSWITCH never sees it."
              error={form.formState.errors.name?.message}
            >
              <Input
                autoComplete="off"
                placeholder="Twilio Elastic SIP Trunk"
                invalid={Boolean(form.formState.errors.name)}
                {...form.register("name")}
              />
            </Field>

            <Field label="Description" htmlFor="gateway-description" hint="What it is for.">
              <Input
                autoComplete="off"
                placeholder="Primary PSTN carrier"
                {...form.register("description")}
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Realm"
                htmlFor="gateway-realm"
                required
                hint="The carrier's SIP domain."
                error={form.formState.errors.realm?.message}
              >
                <Input
                  className="font-mono"
                  autoComplete="off"
                  placeholder="acl.pstn.twilio.com"
                  invalid={Boolean(form.formState.errors.realm)}
                  {...form.register("realm")}
                />
              </Field>
              <Field
                label="Proxy"
                htmlFor="gateway-proxy"
                hint="Where the INVITE goes. Blank means the realm."
                error={form.formState.errors.proxy?.message}
              >
                <Input
                  className="font-mono"
                  autoComplete="off"
                  placeholder="same as the realm"
                  {...form.register("proxy")}
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Username"
                htmlFor="gateway-username"
                required
                error={form.formState.errors.username?.message}
              >
                <Input
                  className="font-mono"
                  autoComplete="off"
                  invalid={Boolean(form.formState.errors.username)}
                  {...form.register("username")}
                />
              </Field>
              <Field
                label={editing ? "Set a new password" : "Password"}
                htmlFor="gateway-password"
                required={!editing}
                hint={
                  editing
                    ? "Leave blank to keep the current one."
                    : "What the carrier issued for this trunk."
                }
                error={form.formState.errors.password?.message}
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  className="font-mono"
                  invalid={Boolean(form.formState.errors.password)}
                  {...form.register("password")}
                />
              </Field>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-text">Sofia parameters</p>
                  <p className="text-xs text-text-muted">
                    Anything else the carrier asks for — <code>caller-id-in-from</code>,{" "}
                    <code>expire-seconds</code>, <code>ping</code>.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setParams((rows) => [...rows, { key: String(nextKey++), name: "", value: "" }])
                  }
                >
                  <Plus />
                  Add
                </Button>
              </div>
              {paramError && <Callout tone="danger">{paramError}</Callout>}
              {params.map((row, i) => (
                <div key={row.key} className="flex items-center gap-2">
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    aria-label={`Parameter ${i + 1} name`}
                    placeholder="caller-id-in-from"
                    value={row.name}
                    onChange={(e) =>
                      setParams((rows) =>
                        rows.map((r) => (r.key === row.key ? { ...r, name: e.target.value } : r)),
                      )
                    }
                  />
                  <Input
                    className="font-mono"
                    autoComplete="off"
                    aria-label={`Parameter ${i + 1} value`}
                    placeholder="true"
                    value={row.value}
                    onChange={(e) =>
                      setParams((rows) =>
                        rows.map((r) => (r.key === row.key ? { ...r, value: e.target.value } : r)),
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove parameter ${i + 1}`}
                    onClick={() => setParams((rows) => rows.filter((r) => r.key !== row.key))}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-xs font-medium text-text">Register</p>
                <p className="text-xs text-text-muted">
                  On for a trunk that authenticates by registering. Off for one that authorises
                  this platform's IP, which is how most elastic trunks work.
                </p>
              </div>
              <Switch
                checked={form.watch("register")}
                onCheckedChange={(v) => form.setValue("register", v)}
                aria-label="Register"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div>
                <p className="text-xs font-medium text-text">Enabled</p>
                <p className="text-xs text-text-muted">
                  A disabled trunk stays chosen by any domain that named it, and simply stops
                  carrying their calls.
                </p>
              </div>
              <Switch
                checked={form.watch("enabled")}
                onCheckedChange={(v) => form.setValue("enabled", v)}
                aria-label="Enabled"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={form.formState.isSubmitting}>
              {editing ? "Save changes" : "Create gateway"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
